import { Trans, useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import PartnerLogo from '~/campaigns/components/PartnerLogo'
import { PartnerDetailsType } from '~/campaigns/types/campaign.types'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import SVGImage from '~/components/Image/SVGImage'
import { Link } from '~/components/Link'
import ROUTES from '~/shared/constants/routes.const'
import usePromoActive from '~/shared/hooks/usePromoActive'
import { AppLanguage } from '~/types/locale'

interface Props {
  className?: string
  /** Replaces the Olympiad sponsor line with a partner logo. */
  partnerDetails?: PartnerDetailsType
}

const DESKTOP_PATH =
  'M1321.15 585.59H10.781C5.65491 585.59 1.5 581.435 1.5 576.309V97.2648C1.5 92.1519 5.63501 88.0018 10.7479 87.9848L278.269 86.9659C283.404 86.9478 287.549 82.7639 287.517 77.6288L287.11 10.8378C287.079 5.68982 291.243 1.49982 296.391 1.49982H1321.15C1326.27 1.49982 1330.43 5.65485 1330.43 10.7808V576.309C1330.43 581.435 1326.27 585.59 1321.15 585.59Z'

const MOBILE_PATH =
  'M1 9.00001V56.2588V664C1 668.418 4.58172 672 9 672H367C371.418 672 375 668.418 375 664V49.8637C375 45.4454 371.418 41.8637 367 41.8637H173.22C168.801 41.8637 165.22 38.2819 165.22 33.8637V9C165.22 4.58172 161.638 1 157.22 1H9C4.58172 1 1 4.58173 1 9.00001Z'

export default function IntroHero({ className = '', partnerDetails }: Props): React.ReactElement {
  const { t } = useTranslation('landing')
  const router = useRouter()
  const promoActive = usePromoActive(partnerDetails)

  const headline =
    router.locale === AppLanguage.NO ? (
      <span className="md:text-7xl">
        <Trans components={{ br: <br /> }} i18nKey="intro.heroTitleJanuaryMastery" t={t}>
          {t('intro.heroTitleJanuaryMastery')}
        </Trans>
      </span>
    ) : (
      t('intro.whatTakesTwoWeeks')
    )

  return (
    <div className={`w-full max-w-screen-2xl mx-auto p-12 md:p-0 ${className}`}>
      {/* Mobile: visible on small screens only — avoids JS breakpoint flicker */}
      <svg
        className="block w-full h-auto z-50 md:hidden"
        fill="none"
        preserveAspectRatio="xMidYMin meet"
        viewBox="0 0 376 673"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d={MOBILE_PATH} stroke="url(#paint0_linear_mobile)" strokeWidth="3" />
        <foreignObject height="80" width="140" x="12" y="-12">
          <div className="w-full h-full flex items-center justify-center">
            {/* Width matches the logo so it centres in the notch. */}
            <Link className="block w-25" to={ROUTES.HOME}>
              <SVGImage alt="logo" srcKey="logos/house-of-math-white.svg" width={100} />
            </Link>
          </div>
        </foreignObject>
        {/* Only the taller partner logo needs the extra headroom. */}
        <foreignObject height="80" width="200" x="175" y={partnerDetails ? -17 : -22}>
          <div className="w-full h-full flex items-center gap-1">
            {partnerDetails ? (
              <PartnerLogo partnerDetails={partnerDetails} />
            ) : (
              <>
                <ResponsiveImage
                  fetchPriority="high"
                  fit="contain"
                  height={30}
                  loading="eager"
                  srcKey="landing/mathOlympic.png"
                  width={50}
                />
                <p className="text-2">{t('intro.mathOlympic')}</p>
              </>
            )}
          </div>
        </foreignObject>
        {/* Above the headline here: under the CTAs it would push them up into the image. */}
        {partnerDetails?.landingHeroNoteKey && (
          <foreignObject height="32" width="350" x="12" y="64">
            <p className="text-center text-sm font-medium leading-8">
              {t(`campaign:${partnerDetails.landingHeroNoteKey}`)}
            </p>
          </foreignObject>
        )}
        <foreignObject height="600" width="350" x="12" y="100">
          <div className="flex flex-col items-start w-full px-2">
            <div className="flex flex-col w-full max-w-xl mt-0 text-center">
              <h1 className="text-3xl font-extrabold uppercase">{headline}</h1>
              <p className="text-md mt-2 px-2 leading-5 text-blue-pastel">
                {' '}
                <Trans i18nKey="intro.rightMath" t={t} />
              </p>
            </div>
            <div className="flex flex-col items-center w-full">
              <ResponsiveImage
                fetchPriority="high"
                fit="contain"
                height={280}
                imageClassName={promoActive ? 'opacity-60' : undefined}
                loading="eager"
                srcKey="landing/heroBlobImage.png"
                width={290}
              />
            </div>
          </div>
        </foreignObject>
        <defs>
          <linearGradient gradientUnits="userSpaceOnUse" id="paint0_linear_mobile" x1="0" x2="376" y1="336" y2="336">
            <stop stopColor="#467AFF" />
            <stop offset="0.0369048" stopColor="#4879FE" />
            <stop offset="0.595238" stopColor="#FB0E97" />
            <stop offset="0.977381" stopColor="#FF9B05" />
            <stop offset="1" stopColor="#FF9B05" />
          </linearGradient>
        </defs>
      </svg>

      {/* Desktop: visible from md and up — CSS-only, no flicker */}
      <svg
        className="hidden md:block w-full h-auto z-50"
        fill="none"
        preserveAspectRatio="xMidYMid meet"
        viewBox="0 0 1332 588"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d={DESKTOP_PATH} stroke="url(#paint0_linear_desktop)" strokeWidth="3" />
        <foreignObject height="80" width="300" x="20" y="5">
          <div className="w-full h-full flex items-center">
            <Link className="block w-60" to={ROUTES.HOME}>
              <SVGImage alt="logo" className="max-w-full" srcKey="logos/house-of-math-white.svg" width={300} />
            </Link>
          </div>
        </foreignObject>
        <foreignObject height="80" width="450" x="310" y="5">
          <div className="w-full h-full flex items-center gap-4">
            {partnerDetails ? (
              <PartnerLogo partnerDetails={partnerDetails} />
            ) : (
              <>
                <ResponsiveImage
                  fetchPriority="high"
                  fit="contain"
                  height={65}
                  imageClassName="min-w-24"
                  loading="eager"
                  srcKey="landing/mathOlympic.png"
                  width={100}
                />
                <p className="text-sm">{t('intro.mathOlympic')}</p>
              </>
            )}
          </div>
        </foreignObject>
        <foreignObject height="500" width="1500" x="30" y="80">
          <div className="flex items-start w-full justify-between px-6">
            <div className="flex flex-col w-full max-w-xl mt-24">
              <h1 className="text-5xl font-extrabold uppercase">{headline}</h1>
              <p className="text-md xl:text-[22.7px] 2xl:text-md text-blue-pastel">
                {' '}
                <Trans i18nKey="intro.rightMath" t={t} />
              </p>
            </div>
          </div>
        </foreignObject>
        <defs>
          <linearGradient
            gradientUnits="userSpaceOnUse"
            id="paint0_linear_desktop"
            x1="1515.18"
            x2="2847.11"
            y1="240.64"
            y2="240.64"
          >
            <stop stopColor="#467AFF" />
            <stop offset="0.0369048" stopColor="#4879FE" />
            <stop offset="0.595238" stopColor="#FB0E97" />
            <stop offset="0.977381" stopColor="#FF9B05" />
            <stop offset="1" stopColor="#FF9B05" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  )
}
